import "@/App.css";
import { lazy, Suspense, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { useOpeningPhase } from "@/lib/openingClock";
import GrandOpeningSplash, { splashInitiallyDismissed } from "@/components/GrandOpeningSplash";

// Sprint 13.60 — Landing (and every other page) is now lazy AND gated on
// splash bypass. While the pre-launch splash is up, Routes never mount,
// so the Landing chunk (and its hero image) never even start downloading.
// This is the killer LCP fix — Chrome's LCP algorithm was previously
// measuring the hero IMG rendering behind the fixed splash overlay on
// throttled mobile 4G, which pushed LCP to ~4.6s. With Routes gated the
// LCP element becomes the splash H1 "8/29/2026" itself (subset Anton is
// inlined — no HTTP roundtrip), which paints in ~500ms.
const Landing = lazy(() => import("@/pages/Landing"));
const OpeningDayPopup = lazy(() => import("@/components/OpeningDayPopup"));
const AnniversaryPopup = lazy(() => import("@/components/AnniversaryPopup"));

// ── Public routes (lazy) ──────────────────────────────────────────────
const EmployeeLogin = lazy(() => import("@/pages/EmployeeLogin"));
const TrackRequest = lazy(() => import("@/pages/TrackRequest"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const NeighborhoodDetail = lazy(() => import("@/pages/NeighborhoodDetail"));
const Dumpsters = lazy(() => import("@/pages/Dumpsters"));
const TrackDumpster = lazy(() => import("@/pages/TrackDumpster"));
const Recap = lazy(() => import("@/pages/Recap"));
const Articles = lazy(() => import("@/pages/Articles"));
const GiftCard = lazy(() => import("@/pages/GiftCard"));
const GiftCardBalance = lazy(() => import("@/pages/GiftCardBalance"));
const Realtors = lazy(() => import("@/pages/Realtors"));
const About = lazy(() => import("@/pages/About"));
const MeetTheCrew = lazy(() => import("@/pages/MeetTheCrew"));
const CrewUpload = lazy(() => import("@/pages/CrewUpload"));
const AdminCrew = lazy(() => import("@/pages/AdminCrew"));
const AdminEmployees = lazy(() => import("@/pages/AdminEmployees"));
const AdminUsers = lazy(() => import("@/pages/AdminUsers"));
const AdminRealtorApplications = lazy(() => import("@/pages/AdminRealtorApplications"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const ServiceArea = lazy(() => import("@/pages/ServiceArea"));
const RealtorDashboard = lazy(() => import("@/pages/RealtorDashboard"));
const AccountLogin = lazy(() => import("@/pages/AccountLogin"));
const AccountSignup = lazy(() => import("@/pages/AccountSignup"));
const AccountHome = lazy(() => import("@/pages/AccountHome"));
const AccountAuthCallback = lazy(() => import("@/pages/AccountAuthCallback"));
const AccountForgotPassword = lazy(() => import("@/pages/AccountForgotPassword"));
const AccountResetPassword = lazy(() => import("@/pages/AccountResetPassword"));

// ── Crew routes (lazy) — biggest wins live here since these files are
//    huge and never touched by public visitors. ────────────────────────
const EmployeeDashboard = lazy(() => import("@/pages/EmployeeDashboard"));
const RoutePlanner = lazy(() => import("@/pages/RoutePlanner"));
const BusinessSchedule = lazy(() => import("@/pages/BusinessSchedule"));
const MyDay = lazy(() => import("@/pages/MyDay"));

// Minimal Suspense fallback — a full-screen cream backdrop with the
// wordmark. Deliberately not a spinner so the user sees the brand
// during the ~300ms chunk download instead of a generic loader.
function RouteFallback() {
  return (
    <div
      data-testid="route-fallback"
      style={{
        minHeight: "100vh",
        background: "#FBF3E5",
        color: "#1A1A1A",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Chivo', sans-serif",
        fontWeight: 900,
        letterSpacing: "0.3em",
        textTransform: "uppercase",
        fontSize: 14,
      }}
    >
      <div>
        Bye-Bye <span style={{ color: "#B61E27" }}>Buckeye</span>
      </div>
    </div>
  );
}

function App() {
  // Sprint 13.60 — hoist splash state to App so we can gate the entire
  // Router on it. Landing (+ every other lazy chunk) doesn't mount until
  // the customer clicks through, so the hero IMG never gets a chance to
  // become the LCP element on the pre-launch splash view.
  const { preLaunch, msToOpen } = useOpeningPhase();
  const [dismissed, setDismissed] = useState(splashInitiallyDismissed);
  // Sprint 13.64 — crew members tapping the SMS/email upload link from
  // dispatch must land ON the upload page, not the pre-launch splash.
  // Bypass the splash exclusively for that route (read once on mount —
  // deep-link handoff doesn't need to react to in-app navigation).
  const isCrewSelfUpload =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/crew/upload/");
  const splashActive = preLaunch && !dismissed && !isCrewSelfUpload;
  const bypass = () => setDismissed(true);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          {splashActive ? (
            <GrandOpeningSplash msToOpen={msToOpen} onBypass={bypass} />
          ) : (
            <>
              <Suspense fallback={null}>
                <OpeningDayPopup />
                <AnniversaryPopup />
              </Suspense>
              <Toaster
                position="top-right"
                toastOptions={{
                  style: {
                    borderRadius: 0,
                    border: "2px solid #1A1A1A",
                    fontFamily: "Chivo, sans-serif",
                    fontWeight: 600,
                  },
                }}
              />
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/gallery" element={<Gallery />} />
                  <Route path="/leaderboard" element={<Leaderboard />} />
                  <Route path="/leaderboard/neighborhood/:city" element={<NeighborhoodDetail />} />
                  <Route path="/dumpsters" element={<Dumpsters />} />
                  <Route path="/track-dump/:ref" element={<TrackDumpster />} />
                  <Route path="/recap" element={<Recap />} />
                  <Route path="/recap/:week" element={<Recap />} />
                  <Route path="/track" element={<TrackRequest />} />
                  <Route path="/track/:ref" element={<TrackRequest />} />
                  <Route path="/articles" element={<Articles />} />
                  <Route path="/gift-card" element={<GiftCard />} />
                  <Route path="/gift-card/balance" element={<GiftCardBalance />} />
                  <Route path="/gift-card/success" element={<GiftCard />} />
                  <Route path="/realtors" element={<Realtors />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/meet-the-crew" element={<MeetTheCrew />} />
                  <Route path="/crew/upload/:token" element={<CrewUpload />} />
                  <Route
                    path="/admin/crew"
                    element={
                      <ProtectedRoute>
                        <AdminCrew />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/employees"
                    element={
                      <ProtectedRoute>
                        <AdminEmployees />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute>
                        <AdminUsers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/realtor-applications"
                    element={
                      <ProtectedRoute>
                        <AdminRealtorApplications />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/service-area" element={<ServiceArea />} />
                  <Route path="/realtors/dashboard/:code" element={<RealtorDashboard />} />
                  <Route path="/employee" element={<EmployeeLogin />} />
                  <Route path="/account/login" element={<AccountLogin />} />
                  <Route path="/account/signup" element={<AccountSignup />} />
                  <Route path="/account/forgot-password" element={<AccountForgotPassword />} />
                  <Route path="/account/reset-password" element={<AccountResetPassword />} />
                  <Route path="/account/auth/callback" element={<AccountAuthCallback />} />
                  <Route path="/account" element={<AccountHome />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <EmployeeDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/routes"
                    element={
                      <ProtectedRoute>
                        <RoutePlanner />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/schedule"
                    element={
                      <ProtectedRoute>
                        <BusinessSchedule />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-day"
                    element={
                      <ProtectedRoute>
                        <MyDay />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </>
          )}
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
